import type { IComponentController, IFilterService, IScope } from "angular";
import { head, last } from "lodash";
import type { Duration } from "moment-timezone";
import { duration } from "moment-timezone";

import type { IScheduleTimePeriod } from "App/Modules/Models";
import { DayType } from "App/Modules/Models";

const INTERVAL_DURATION = 10;
const INTERVALS_COUNT = 144;
const DAY_IN_SECONDS = 86400;

export default class TimeRangePickerComponentController implements IComponentController {
    public static $inject: string[] = ["$scope", "$filter"];

    public colorTheme: string;

    public ranges: IScheduleTimePeriod[];

    public staticRanges: IScheduleTimePeriod[];

    public dayType: number;

    public isDisabled: boolean;

    public mappedRanges: number[][];

    public mappedStaticRanges: number[][];

    public intervalsCount = INTERVALS_COUNT;

    constructor(private readonly $scope: IScope, private readonly $filter: IFilterService) {
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.tooltipMapper = this.tooltipMapper.bind(this);
    }

    public $onInit() {
        const unsub = this.$scope.$watch("$ctrl.ranges", (newValue: IScheduleTimePeriod[]) => {
            if (!newValue) {
                return;
            }

            this.mappedRanges = this.mapSections(newValue);

            unsub();
        });

        this.$scope.$watch("$ctrl.mappedRanges", (newValue: number[][]) => {
            if (!newValue) {
                return;
            }

            this.mapSectionsBack(newValue);
        });

        if (this.staticRanges) {
            this.mappedStaticRanges = this.mapSections(this.staticRanges);

            this.$scope.$watch("$ctrl.staticRanges", (newValue: IScheduleTimePeriod[]) => {
                if (!newValue) {
                    return;
                }

                this.mappedStaticRanges = this.mapSections(newValue);
            });
        }
    }

    private mapSections(ranges: IScheduleTimePeriod[]) {
        return ranges.map((range) => {
            return [this.fromDurationToNumber(range.startTime), this.fromDurationToNumber(range.stopTime) - 1];
        });
    }

    private mapSectionsBack(ranges: number[][]) {
        this.ranges = ranges.map((range) => {
            const start = head(range) * INTERVAL_DURATION;
            const stop = (last(range) + 1) * INTERVAL_DURATION;
            const startTime = duration(start * 60, "seconds");
            const stopTime =
                last(range) === INTERVALS_COUNT - 1
                    ? duration(stop * 60 - 1, "seconds")
                    : duration(stop * 60, "seconds");

            return {
                day: DayType[this.dayType] as any,
                dayScheduleId: null,
                id: null,
                startTime,
                stopTime,
            };
        });
    }

    private fromDurationToNumber(time: Duration) {
        return Math.round((time.asSeconds() / DAY_IN_SECONDS) * INTERVALS_COUNT);
    }

    private tooltipMapper(range: number[]) {
        const durationFilter: (duration: Duration) => string = this.$filter("mmDurationToLocalTimeString");

        return range
            .slice()
            .sort((a, b) => a - b)
            .map((interval, index) => {
                const adjustedIfLast = index === range.length - 1 ? interval + 1 : interval;

                return durationFilter(duration(adjustedIfLast * INTERVAL_DURATION, "minutes"));
            });
    }
}
